import { useState, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';
import { errMsg } from '../lib/format';

/**
 * Hook central del Editor APU: data-fetching, mutaciones y estado de autosave.
 */
export function useQuotationEditor(quotationId) {
  const queryClient = useQueryClient();
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const savedTimer = useRef(null);

  const markSaving = useCallback(() => {
    setSaveState('saving');
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  const markSaved = useCallback(() => {
    setSaveState('saved');
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveState('idle'), 2500);
  }, []);

  const { data: quotation, isLoading } = useQuery({
    queryKey: ['quotation-full', quotationId],
    queryFn: async () => (await api.get(`/quotations/${quotationId}/full`)).data,
    enabled: !!quotationId,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['quotation-full', quotationId] });
    queryClient.invalidateQueries({ queryKey: ['quotations'] });
  }, [queryClient, quotationId]);

  const useEditorMutation = (fn, { successMsg, silent } = {}) => useMutation({
    mutationFn: fn,
    onMutate: markSaving,
    onSuccess: () => {
      invalidate();
      markSaved();
      if (successMsg && !silent) toast.success(successMsg);
    },
    onError: (e) => {
      setSaveState('error');
      toast.error(errMsg(e));
    },
  });

  
  const updateQuotation = useEditorMutation(async (patch) => (await api.put(`/quotations/${quotationId}`, patch)).data, { silent: true });
  const addGroup = useEditorMutation(async (nombre) => (await api.post('/groups', { quotation_id: quotationId, nombre_grupo: nombre })).data, { successMsg: 'Grupo agregado' });
  const updateGroup = useEditorMutation(async ({ groupId, patch }) => (await api.put(`/groups/${groupId}`, patch)).data, { silent: true });
  const deleteGroup = useEditorMutation(async (groupId) => (await api.delete(`/groups/${groupId}`)).data, { successMsg: 'Grupo eliminado' });
  const reorderGroups = useEditorMutation(async (groupIds) => (await api.post(`/quotations/${quotationId}/reorder-groups`, { group_ids: groupIds })).data, { silent: true });
  const reorderConcepts = useEditorMutation(async ({ groupId, conceptIds }) => (await api.post(`/groups/${groupId}/reorder-concepts`, { concept_ids: conceptIds })).data, { silent: true });

  const addConcept = useEditorMutation(async (payload) => (await api.post('/concepts', payload)).data, { successMsg: 'Concepto agregado' });
  const addConceptFromCatalog = useEditorMutation(async (payload) => (await api.post('/concepts/from-catalog', payload)).data, { successMsg: 'Concepto agregado desde catálogo' });
  const updateConcept = useEditorMutation(async ({ conceptId, patch }) => (await api.put(`/concepts/${conceptId}`, patch)).data, { silent: true });
  const deleteConcept = useEditorMutation(async (conceptId) => (await api.delete(`/concepts/${conceptId}`)).data, { successMsg: 'Concepto eliminado' });
  const duplicateConcept = useEditorMutation(async (conceptId) => (await api.post(`/concepts/${conceptId}/duplicate`)).data, { successMsg: 'Concepto duplicado' });
  const moveConcept = useEditorMutation(async ({ conceptId, targetGroupId }) => (await api.post(`/concepts/${conceptId}/move`, { target_group_id: targetGroupId })).data, { successMsg: 'Concepto movido' });

  const addApuItem = useEditorMutation(async ({ type, payload }) => (await api.post(`/${type}`, payload)).data, { silent: true });
  const addApuFromCatalog = useEditorMutation(async ({ type, payload }) => (await api.post(`/${type}/from-catalog`, payload)).data, { silent: true });
  const updateApuItem = useEditorMutation(async ({ type, itemId, patch }) => (await api.put(`/${type}/${itemId}`, patch)).data, { silent: true });
  const deleteApuItem = useEditorMutation(async ({ type, itemId }) => (await api.delete(`/${type}/${itemId}`)).data, { silent: true });

  const finalize = useEditorMutation(async () => (await api.post(`/quotations/${quotationId}/finalize`)).data, { successMsg: 'Cotización finalizada: precios congelados' });
  const reopen = useEditorMutation(async () => (await api.post(`/quotations/${quotationId}/reopen`)).data, { successMsg: 'Cotización reabierta para edición' });
  const recalculate = useEditorMutation(async () => (await api.post(`/quotations/${quotationId}/recalculate`)).data, { successMsg: 'Totales recalculados' });
  

  return {
    quotation,
    isLoading,
    saveState,
    frozen: quotation?.status === 'finalized',
    updateQuotation,
    addGroup, updateGroup, deleteGroup, reorderGroups, reorderConcepts,
    addConcept, addConceptFromCatalog, updateConcept, deleteConcept, duplicateConcept, moveConcept,
    addApuItem, addApuFromCatalog, updateApuItem, deleteApuItem,
    finalize, reopen, recalculate,
  };
}
